import { z } from 'zod';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { CUSTOMER_TYPES } from '@/lib/organization/company-fields';

// ============================================================================
// REGISTRATION SCHEMAS
// ============================================================================

/**
 * Password validation that matches Supabase Auth requirements.
 * Supabase is configured to require: lowercase, uppercase, number, special character.
 */
export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(
    /[!@#$%^&*()_+\-=\[\]{};':"\\|<>?,./`~]/,
    'Password must contain at least one special character'
  );

/**
 * Helper to check individual password requirements (for UI feedback)
 */
export const passwordRequirements = [
  { id: 'length', label: 'At least 8 characters', test: (pw: string) => pw.length >= 8 },
  { id: 'lowercase', label: 'One lowercase letter (a-z)', test: (pw: string) => /[a-z]/.test(pw) },
  { id: 'uppercase', label: 'One uppercase letter (A-Z)', test: (pw: string) => /[A-Z]/.test(pw) },
  { id: 'number', label: 'One number (0-9)', test: (pw: string) => /[0-9]/.test(pw) },
  {
    id: 'special',
    label: 'One special character (!@#$...)',
    test: (pw: string) => /[!@#$%^&*()_+\-=\[\]{};':"\\|<>?,./`~]/.test(pw),
  },
] as const;

export const registerSchema = z.object({
  email: z.string().email('Invalid email address').max(254, 'Email too long'),
  password: passwordSchema,
  firstName: z.string().trim().min(1, 'First name is required').max(50, 'First name too long'),
  lastName: z.string().trim().max(50, 'Last name too long').optional(),
  phoneRaw: z
    .string()
    .trim()
    .min(1, 'Phone number is required')
    .refine((v) => isValidPhoneNumber(v, 'RO'), { message: 'Invalid phone number' }),
  organizationName: z
    .string()
    .min(2, 'Organization name must be at least 2 characters')
    .max(100, 'Organization name too long')
    .optional(),
  // Terms versioning metadata (set by client at signup time)
  terms_version: z.string().optional(),
  terms_accepted_at: z.string().optional(),
  privacy_version: z.string().optional(),
  privacy_accepted_at: z.string().optional(),
  aup_version: z.string().optional(),
  aup_accepted_at: z.string().optional(),
  // Traffic attribution — how did this user find us?
  attribution: z.record(z.string(), z.string().nullable()).optional(),
});

export type RegisterInput = z.infer<typeof registerSchema>;

export function composeFullName(firstName: string, lastName?: string): string {
  return [firstName?.trim(), lastName?.trim()].filter(Boolean).join(' ');
}

// ============================================================================
// FUNNEL INTENT/PLAN TAXONOMY (2026-07-10 landing fleet rebuild, WS2 lane F1)
// ============================================================================
// Landing CTAs carry the visitor's chosen funnel (?intent=crm|bot|both) and, for
// pricing cards, the chosen tier (?plan=basic|pro) — see PricingSection.tsx and
// WhatsAppDemoForm.tsx (src/features/marketing/components/). Whitelist-validated
// here so the value is trusted before it is persisted into signUp user_metadata
// (RegisterForm.tsx) and later read back from it (onboarding/page.tsx).

export const FUNNEL_INTENTS = ['crm', 'bot', 'both'] as const;
export type FunnelIntent = (typeof FUNNEL_INTENTS)[number];

export const FUNNEL_PLANS = ['basic', 'pro'] as const;
export type FunnelPlan = (typeof FUNNEL_PLANS)[number];

const funnelIntentSchema = z.enum(FUNNEL_INTENTS);
const funnelPlanSchema = z.enum(FUNNEL_PLANS);

/**
 * Whitelist-validate the funnel intent/plan query params (or persisted
 * user_metadata values) against the taxonomy above. Anything unrecognized
 * (missing, unknown value, string[] from a repeated query param, empty string)
 * resolves to `undefined` rather than throwing — fail-soft by design: this is
 * a UX preselection, not a security gate, and both values are opaque enum
 * tags that are NEVER used as a navigation target (no open-redirect surface
 * to guard against). Reused by both src/app/auth/register/page.tsx (from
 * `searchParams`) and the onboarding page (from `user.user_metadata`, defense
 * in depth against a hand-edited/legacy metadata value).
 */
export function parseFunnelIntent(
  intentParam: string | string[] | null | undefined,
  planParam: string | string[] | null | undefined
): { intent?: FunnelIntent; plan?: FunnelPlan } {
  const intentResult =
    typeof intentParam === 'string' ? funnelIntentSchema.safeParse(intentParam) : undefined;
  const planResult =
    typeof planParam === 'string' ? funnelPlanSchema.safeParse(planParam) : undefined;

  return {
    intent: intentResult?.success ? intentResult.data : undefined,
    plan: planResult?.success ? planResult.data : undefined,
  };
}

// ============================================================================
// ONBOARDING SCHEMAS
// ============================================================================

export const organizationProfileSchema = z.object({
  name: z.string().min(2, 'Organization name required'),
  industry: z.string().min(1, 'Industry required'),
  phone_number: z
    .string()
    .regex(/^\+?\d{10,15}$/, 'Invalid phone number')
    .nullable()
    .optional(),
  team_size: z.string().min(1, 'Team size required'),
  website: z.string().url('Invalid website URL').nullable().optional(),
  currency: z.string().length(3, 'Currency must be a 3-letter code').optional(),
  /**
   * Who the org sells to. Optional so a partially-filled or older client payload
   * still validates; an absent value leaves settings.customer_type untouched, which
   * resolves to 'both' — today's behaviour. Enum derived from the one union in
   * @/lib/organization/company-fields, never re-spelled here.
   */
  customer_type: z.enum(CUSTOMER_TYPES).optional(),
});

export type OrganizationProfileInput = z.infer<typeof organizationProfileSchema>;

export const planSelectionSchema = z.object({
  tier: z.enum(['free', 'basic', 'pro', 'enterprise']),
  modules: z.array(z.string()).optional(),
});

export type PlanSelectionInput = z.infer<typeof planSelectionSchema>;

// ============================================================================
// PROFILE MANAGEMENT SCHEMAS
// ============================================================================

export const updateProfileSchema = z
  .object({
    fullName: z.string().min(2, 'Name must be at least 2 characters').max(100, 'Name too long'),
  })
  .strict();

export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(8, 'Password must be at least 8 characters'),
    // New password must meet the same strength requirements as signup
    newPassword: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

// ============================================================================
// COMPANY BRANDING SCHEMAS
// ============================================================================

export const companyBrandingSchema = z.object({
  companyName: z
    .string()
    .min(2, 'Company name must be at least 2 characters')
    .max(100, 'Company name too long'),
  companyLogoUrl: z.string().url('Invalid logo URL').nullable().optional(),
});

export type CompanyBrandingInput = z.infer<typeof companyBrandingSchema>;

// HEX color validation regex (e.g., #FF5733)
const hexColorRegex = /^#[0-9A-Fa-f]{6}$/;

export const brandColorsSchema = z.object({
  primaryColor: z
    .string()
    .regex(hexColorRegex, 'Primary color must be a valid HEX color (e.g., #FF5733)'),
  accentColor: z
    .string()
    .regex(hexColorRegex, 'Accent color must be a valid HEX color (e.g., #D97706)'),
});

export type BrandColorsInput = z.infer<typeof brandColorsSchema>;

// ============================================================================
// HSL THEMING SCHEMAS
// ============================================================================

/**
 * Brand hue (0-360) - the single color that drives the entire palette
 */
export const brandHueSchema = z.object({
  brandHue: z.number().int().min(0).max(360),
});

export type BrandHueInput = z.infer<typeof brandHueSchema>;

/**
 * Appearance preset - controls saturation/contrast globally
 */
export const appearancePresetSchema = z.object({
  appearancePreset: z.enum(['balanced', 'washed', 'contrast']),
});

export type AppearancePresetInput = z.infer<typeof appearancePresetSchema>;

/**
 * Combined theme settings update
 */
export const themeSettingsSchema = z.object({
  brandHue: z.number().int().min(0).max(360).optional(),
  appearancePreset: z.enum(['balanced', 'washed', 'contrast']).optional(),
});

export type ThemeSettingsInput = z.infer<typeof themeSettingsSchema>;
